import {
  Column,
  DataGrid,
  Editing,
  Paging,
  StateStoring,
  DataGridRef,
  Toolbar,
  Item,
  RequiredRule,
  Lookup,
} from 'devextreme-react/data-grid'
import type { CellPreparedEvent, InitNewRowEvent } from 'devextreme/ui/data_grid'
import { useCallback, useEffect, useRef, useState } from 'react'
import notify from 'devextreme/ui/notify'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import { promemoria_task, stato_promemoria_task, user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import DataSource from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useAzureManager, useTokenRefresh } from '@/auth/azure/azureManager'
import { TaskEditorPromemoriasGridProps } from '@/routes/tasks/TaskEditor.types'
import { isUserRoleAllowed } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'

const TaskEditorPromemoriasGrid = (props: TaskEditorPromemoriasGridProps) => {
  const { task } = props
  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const { service } = useQsAdminApiManager()
  const [stati, setStati] = useState<stato_promemoria_task[]>([])
  const [utente, setUtente] = useState<user>()
  const token = useTokenRefresh()
  const { userInfo } = useAzureManager()

  const promemoriasDataSOurce = new DataSource({
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/promemoria_task`,
      key: 'id',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: [['task.id', task.id]],
    expand: ['stato, utente'],
  })

  const onCellPrepared = (e: CellPreparedEvent) => {}

  const getStati = useCallback(async () => {
    const statiTask = await service.stato_promemoria_task().query((builder, stato) => {
      builder.select('id', 'nome')
      builder.orderBy(stato.id.asc())
    })
    return statiTask.data.value
  }, [service])

  const getUtente = useCallback(async () => {
    const utenteData = await service.user().query((builder, impiegato) => {
      builder.filter(impiegato.email.eq(userInfo?.email ?? ''))
      builder.select('id', 'fullname')
    })
    return utenteData.data.value
  }, [service, userInfo?.email])

  useEffect(() => {
    getStati().then((result) => {
      setStati(result)
    })
    getUtente().then((result) => {
      setUtente(result[0])
    })
  }, [getStati, getUtente])

  return (
    <>
      <DataGrid
        ref={gridRef}
        id={`task-promemorias-grid`}
        dataSource={promemoriasDataSOurce}
        noDataText={`Nessun promemoria associato al task`}
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnReordering={true}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        onCellPrepared={onCellPrepared}
        width="100%"
        onInitNewRow={(e: InitNewRowEvent) => {
          e.data.task = task
          e.data.note = ''
          e.data.stato = stati.find((stato) => stato.nome === 'ATTIVO')
          e.data.data_inizio = new Date().toISOString()
          e.data.utente = utente
        }}
      >
        <StateStoring enabled={true} type="localStorage" storageKey={`task-promemorias-grid`} savingTimeout={50} />
        <Paging defaultPageSize={8} />
        <Editing
          allowAdding={true}
          allowDeleting={true}
          allowUpdating={true}
          mode="batch"
          selectTextOnEditStart={true}
          startEditAction={'click'}
        />
        <Column dataField={'id'} caption={'ID'} visible={false} />,
        <Column
          dataField={'stato'}
          caption={'RISOLTO'}
          dataType={'boolean'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
          calculateCellValue={(rowData: promemoria_task) => {
            return rowData.stato?.nome === 'DISATTIVO'
          }}
          setCellValue={(newData: promemoria_task, value: boolean) => {
            newData.stato = value
              ? stati.find((stato) => stato.nome === 'DISATTIVO')
              : stati.find((stato) => stato.nome === 'ATTIVO')
          }}
        ></Column>
        <Column dataField={'note'} caption={'NOTE'}>
          <RequiredRule message="Note obbligatorie"></RequiredRule>
        </Column>
        <Column dataField={'data_inizio'} caption={'DATA INIZIO'} dataType="date">
          <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
        </Column>
        <Column
          dataField={'utente.fullname'}
          caption={'UTENTE'}
          editorOptions={{ readOnly: true }}
          visible={isUserRoleAllowed(userInfo?.roles, [
            Roles.GlobalAdministrator,
            Roles.Administrator,
            Roles.Supervisor,
          ])}
        ></Column>
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="addRowButton"
            options={{
              hint: 'Nuovo promemoria',
              text: 'Nuovo promemoria',
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="saveButton"
            options={{
              hint: 'Salva',
              text: 'Salva',
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="revertButton"
            options={{
              hint: 'Annulla modifiche',
              text: 'Annulla modifiche',
              stylingMode: 'text',
            }}
          ></Item>
        </Toolbar>
      </DataGrid>
    </>
  )
}

export default TaskEditorPromemoriasGrid
